import { z } from "zod";

const generativeUiPartVersion = 1;
const boundedLabelSchema = z.string().min(1).max(200);
const boundedPromptSchema = z.string().min(1).max(2_000);

const generativeUiSourceRefSchema = z
  .object({
    citationId: z.string().min(1).max(200),
    label: boundedLabelSchema,
  })
  .strict();

export type GenerativeUiSourceRef = z.infer<typeof generativeUiSourceRefSchema>;

const generativeUiActionSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("follow_up"),
      label: boundedLabelSchema,
      prompt: boundedPromptSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("download"),
      label: boundedLabelSchema,
      format: z.enum(["csv", "json"]),
    })
    .strict(),
  z
    .object({
      type: z.literal("save_artifact"),
      label: boundedLabelSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("create_dashboard"),
      label: boundedLabelSchema,
    })
    .strict(),
]);

export type GenerativeUiAction = z.infer<typeof generativeUiActionSchema>;

const partBaseShape = {
  version: z.literal(generativeUiPartVersion),
  id: z.string().min(1).max(200),
  title: boundedLabelSchema,
  sourceRefs: z.array(generativeUiSourceRefSchema).max(20),
  actions: z.array(generativeUiActionSchema).max(10),
};

const metricSchema = z
  .object({
    label: boundedLabelSchema,
    value: z.union([z.string().min(1).max(500), z.number()]),
  })
  .strict();

const metricGroupPartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("metric_group"),
    metrics: z.array(metricSchema).min(1).max(12),
  })
  .strict();

export type MetricGroupPart = z.infer<typeof metricGroupPartSchema>;

const maxDataTableColumns = 20;

const dataTableColumnSchema = z
  .object({
    key: z.string().min(1).max(100),
    label: boundedLabelSchema,
  })
  .strict();

const dataTableCellSchema = z.union([
  z.string().max(500),
  z.number(),
  z.boolean(),
  z.null(),
]);

const dataTablePartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("data_table"),
    columns: z.array(dataTableColumnSchema).min(1).max(maxDataTableColumns),
    rows: z.array(z.record(z.string().max(100), dataTableCellSchema)).max(100),
  })
  .strict();

export type DataTablePart = z.infer<typeof dataTablePartSchema>;

const chartSeriesSchema = z
  .object({
    label: boundedLabelSchema,
    values: z.array(z.number()).min(1).max(100),
  })
  .strict();

const chartPartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("chart"),
    chartType: z.enum(["line", "bar"]),
    labels: z.array(boundedLabelSchema).min(1).max(100),
    series: z.array(chartSeriesSchema).min(1).max(10),
  })
  .strict();

export type ChartPart = z.infer<typeof chartPartSchema>;

const thresholdControlPartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("threshold_control"),
    value: z.number(),
    min: z.number(),
    max: z.number(),
    step: z.number().positive(),
  })
  .strict();

export type ThresholdControlPart = z.infer<typeof thresholdControlPartSchema>;

const insightPartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("insight"),
    body: z.string().min(1).max(2_000),
  })
  .strict();

export type InsightPart = z.infer<typeof insightPartSchema>;

const drilldownActionPartSchema = z
  .object({
    ...partBaseShape,
    type: z.literal("drilldown_action"),
    prompt: boundedPromptSchema,
  })
  .strict();

export type DrilldownActionPart = z.infer<typeof drilldownActionPartSchema>;

export const generativeUiPartSchema = z
  .discriminatedUnion("type", [
    metricGroupPartSchema,
    dataTablePartSchema,
    chartPartSchema,
    thresholdControlPartSchema,
    insightPartSchema,
    drilldownActionPartSchema,
  ])
  .superRefine((part, ctx) => {
    if (part.type === "data_table") {
      for (const [index, row] of part.rows.entries()) {
        if (Object.keys(row).length > maxDataTableColumns) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `row key count must not exceed ${maxDataTableColumns}`,
            path: ["rows", index],
          });
        }
      }
      return;
    }

    if (part.type === "chart") {
      for (const [index, series] of part.series.entries()) {
        if (series.values.length !== part.labels.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "series values length must match labels length",
            path: ["series", index, "values"],
          });
        }
      }
      return;
    }

    if (part.type === "threshold_control") {
      if (part.min > part.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "min must be less than or equal to max",
          path: ["min"],
        });
      }
      if (part.value < part.min || part.value > part.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "value must be between min and max",
          path: ["value"],
        });
      }
    }
  });

export type GenerativeUiPart = z.infer<typeof generativeUiPartSchema>;

const generativeUiEnvelopeSchema = z
  .object({
    version: z.number().int(),
  })
  .passthrough();

export function unsupportedGenerativeUiVersion(value: unknown): number | null {
  const envelope = generativeUiEnvelopeSchema.safeParse(value);
  if (!envelope.success || envelope.data.version === generativeUiPartVersion) {
    return null;
  }
  return envelope.data.version;
}

export function parseGenerativeUiPart(value: unknown): GenerativeUiPart | null {
  const parsed = generativeUiPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
